'use client'

import { Component } from 'react'
import { isStaleChunkError, reportClientError } from '@/app/_lib/clientErrorReporter'

/**
 * Client component tree error boundary, mounted in the root layout.
 *
 * Sits outside Next's own route-segment boundary (app/error.js), so it catches
 * what escapes that — errors thrown in the layout's own client subtree.
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        // Stale code after a deploy - auto-reload to get fresh chunks.
        // Chunk errors only: a broader check could reload-loop from render.
        if (isStaleChunkError(error) && typeof window !== 'undefined') {
            window.location.reload()
            return { hasError: false, error: null }
        }

        return { hasError: true, error }
    }

    componentDidCatch(error, errorInfo) {
        reportClientError({
            error,
            type: 'react',
            componentStack: errorInfo?.componentStack,
        })
    }

    render() {
        if (this.state.hasError) {
            // Render fallback UI
            return (
                <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
                    <h2 className="text-2xl font-light mb-4">Something went wrong</h2>
                    <p className="text-light mb-6">
                        We&apos;ve been notified and are looking into it.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="btn btn-primary"
                    >
                        Reload Page
                    </button>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
